import type { IconName } from "@/components/ui/icons";
import {
  isComputerWorkToastPinned,
  isDrivingComputer,
} from "@/lib/work-toast-computer-pin";
import { resultDetailLine } from "@/lib/work-toast-result-summary";
import { activityEntryLabel } from "@/lib/work-toast-tool-labels";
import type { WorkToastItem } from "@/stores/work-toast-store";
import { workToastWord } from "./toast-status";
import { translate } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { agentActivityUiMessages } from "@/i18n/messages/agent-activity-ui";
import { workToastActionIcon } from "./work-toast-action-icon";

export type WorkToastHoverId = "stop" | "mic" | "type" | "deny" | "allow";
export type WorkToastMenuId =
  | "open-chat"
  | "open-task"
  | "open-in-app"
  | "dismiss";
export type WorkToastClick = "open-chat" | "open-task" | "expand" | "none";

export interface WorkToastBehavior {
  hover: WorkToastHoverId[];
  click: WorkToastClick;
  menu: WorkToastMenuId[];
}

/** Native workers pause through their task; agents and standalone computer
 *  runs stop over the WS. A route-less prompt preview has no `computer`
 *  activity because it has no execution yet. */
export function canStopWorkToast(item: WorkToastItem): boolean {
  if (item.source === "native_worker")
    return Boolean(item.projectId && item.taskId);
  if (item.source === "computer") return Boolean(item.computer);
  return item.source === "agent";
}

/** A settled item opens its conversation, or expands when none is linked. */
function settledClick(item: WorkToastItem): WorkToastClick {
  return item.conversationId ? "open-chat" : "expand";
}

/** Spec §4.2: what hover, the pill body and ⋮ offer per state. */
export function workToastBehavior(
  item: WorkToastItem,
  {
    followUpAvailable,
    micAvailable,
  }: { followUpAvailable: boolean; micAvailable: boolean },
): WorkToastBehavior {
  const chat: WorkToastMenuId[] = item.conversationId ? ["open-chat"] : [];
  const task: WorkToastMenuId[] =
    item.projectId && item.taskId ? ["open-task"] : [];
  const openInApp: WorkToastMenuId[] =
    (item.openTargets?.length ?? 0) > 0 ? ["open-in-app"] : [];
  const replyHover: WorkToastHoverId[] = micAvailable
    ? ["mic", "type"]
    : ["type"];
  switch (item.state) {
    case "working":
      return {
        hover: canStopWorkToast(item) ? ["stop"] : [],
        click: item.conversationId
          ? "open-chat"
          : item.source === "computer"
            ? "expand"
            : "none",
        menu: [...chat, "dismiss"],
      };
    case "question":
      // No Dismiss: a question pill leaves when the question resolves (spec §4.2).
      return { hover: replyHover, click: "expand", menu: chat };
    case "approval":
      return {
        hover: ["deny", "allow"],
        click: "expand",
        menu: [...task, "dismiss"],
      };
    case "blocked":
      return {
        hover: [],
        click:
          item.answerDelivery && chat.length
            ? "open-chat"
            : task.length
              ? "open-task"
              : "expand",
        menu: [...(item.answerDelivery ? chat : []), ...task, "dismiss"],
      };
    case "done":
      return {
        hover: followUpAvailable ? replyHover : [],
        click: settledClick(item),
        menu: [...chat, ...openInApp, "dismiss"],
      };
    case "failed":
    case "stopped":
      // Their card has nothing to expand into: open where the work happened.
      return {
        hover: [],
        click: settledClick(item),
        menu: [...chat, "dismiss"],
      };
  }
}

export function workToastTitle(
  item: WorkToastItem,
  projectName: string | null,
  taskTitle: string | null,
  conversationTitle: string | null = null,
): string {
  // Native workers are helpers, titled by their task; `agentName` stays
  // "Denker" for the avatar. A task run's chat title (a task chat) already
  // reads "Denker helper · <task>".
  if (item.source === "native_worker") {
    const helper = translate(toastsMessages, "denkerHelper");
    const task = taskTitle ?? item.detail;
    return task ? `${helper} · ${task}` : helper;
  }
  if (conversationTitle?.trim()) return conversationTitle.trim();
  return projectName ? `${item.agentName} · ${projectName}` : item.agentName;
}

/** A working item's one current step: "Slack, click" for a run driving the
 *  computer, else its latest ring entry. Empty when there is nothing to say. */
function currentStep(item: WorkToastItem): string {
  if (isDrivingComputer(item) && item.computer) {
    const { appLabel, actionLabel, actionActivityLabel } = item.computer;
    return (
      activityEntryLabel({
        app: appLabel ?? undefined,
        action: actionLabel ?? undefined,
        actionLabel: actionActivityLabel ?? undefined,
      }) || translate(toastsMessages, "usingYourComputer")
    );
  }
  const latest =
    item.state === "working"
      ? item.activity?.[item.activity.length - 1]
      : undefined;
  return latest ? activityEntryLabel(latest) : "";
}

/** The pill's second line. Never a project name: the title already carries it. */
export function workToastDetail(
  item: WorkToastItem,
  taskTitle: string | null,
): string {
  if (item.answerDelivery) return item.detail;
  switch (item.state) {
    case "question":
      return item.question?.question ?? item.detail;
    case "approval":
      return item.approval?.title ?? item.detail;
    case "done":
      return (
        (item.resultText ? resultDetailLine(item.resultText) : item.detail) ||
        workToastWord(item.state)
      );
    case "failed":
      return item.detail || workToastWord(item.state);
    default: {
      // A native worker's detail repeats its title, so its state stands in.
      const own =
        item.source === "native_worker" ? "" : (taskTitle ?? item.detail);
      const screenWork =
        item.source === "computer" && item.state === "working"
          ? translate(agentActivityUiMessages, "ui.working_on_screen")
          : "";
      return (
        currentStep(item) || own || screenWork || workToastWord(item.state)
      );
    }
  }
}

export function workToastCardSubtitle(item: WorkToastItem): string {
  if (item.state === "question")
    return translate(toastsMessages, "needsYourAnswer");
  if (item.state === "approval")
    return translate(toastsMessages, "stateApproval");
  return currentStep(item) || workToastWord(item.state);
}

/** Icon before the step line: the action of a run driving the computer, else
 *  a mark on computer and app steps. Ordinary tool steps carry none, and so
 *  does every line that is not a step: a question, a result, a state word. */
export function workToastDetailIcon(item: WorkToastItem): IconName | undefined {
  if (item.state !== "working") return undefined;
  if (isDrivingComputer(item)) {
    const actionKey = item.computer?.actionKey;
    return actionKey ? workToastActionIcon(actionKey) : undefined;
  }
  const latest = item.activity?.[item.activity.length - 1];
  if (latest?.kind === "computer" && latest.action) return "Zap";
  return isComputerWorkToastPinned(item) ? "Wrench" : undefined;
}

export type WorkToastBodyPart =
  | "question"
  | "approval"
  | "result"
  | "error"
  | "blocked"
  | "activity"
  | "screenshot"
  | "fields";

/** What a blocked card can do: reopen the chat of an undelivered reply, or open its task. */
export function workToastBlockedAction(
  item: WorkToastItem,
): "open-chat" | "open-task" | null {
  if (item.answerDelivery && item.conversationId) return "open-chat";
  return item.projectId && item.taskId ? "open-task" : null;
}

/** Text of a `result` or `blocked` part. Never a project name. A failure's
 *  `error` part is its `detail`; its `result` is the partial answer. */
export function workToastBodyText(
  item: WorkToastItem,
  taskTitle: string | null,
): string {
  if (item.state !== "blocked") return item.resultText ?? item.detail;
  if (item.answerDelivery) return item.detail;
  // A native worker's title is its task, so there is nothing left to say.
  return item.source === "native_worker" ? "" : (taskTitle ?? item.detail);
}

type WorkToastBodyOptions = {
  followUpAvailable: boolean;
  taskTitle: string | null;
};

/** What the item's state itself has to show, before any fill receipt. */
function statusParts(
  item: WorkToastItem,
  { followUpAvailable, taskTitle }: WorkToastBodyOptions,
): WorkToastBodyPart[] {
  switch (item.state) {
    case "question":
      return item.question ? ["question"] : [];
    case "approval":
      return item.approval ? ["approval"] : [];
    case "done":
      return item.resultText || item.detail || followUpAvailable
        ? ["result"]
        : [];
    case "blocked":
      return workToastBlockedAction(item) || workToastBodyText(item, taskTitle)
        ? ["blocked"]
        : [];
    case "failed":
      return [
        ...(item.detail ? (["error"] as const) : []),
        ...(item.resultText ? (["result"] as const) : []),
      ];
    case "stopped":
      return [];
    case "working": {
      const parts: WorkToastBodyPart[] = item.activity?.length
        ? ["activity"]
        : [];
      if (isDrivingComputer(item) && item.computerScreenshots?.length)
        parts.unshift("screenshot");
      // A final message can precede terminal status. Show the same assistant
      // text once while its streamed activity is still on screen.
      const repeatsActivity = item.activity
        ?.slice(-3)
        .some(
          (entry) =>
            entry.kind === "text" &&
            (entry.textFull ?? entry.text) === item.resultText,
        );
      return item.resultText && !repeatsActivity ? [...parts, "result"] : parts;
    }
  }
}

/** The expanded card's body, in order. Empty: nothing worth expanding for. */
export function workToastBodyParts(
  item: WorkToastItem,
  options: WorkToastBodyOptions,
): WorkToastBodyPart[] {
  if (item.state === "stopped") return [];
  const parts = statusParts(item, options);
  // The names of the fields a run filled stay as its receipt.
  const { actionKey, fieldNames } = item.computer ?? {};
  const filled = actionKey === "field-edit" && Boolean(fieldNames?.length);
  return filled ? [...parts, "fields"] : parts;
}
